/**
 * MenuItemSearchSection Component
 * Inline menu item search that collapses to a card after selection
 * Includes inline suggest form with fuzzy duplicate detection
 */

import { menuItemsListQueryOptions, menuItemsSearchQueryOptions } from '@/api/queries/menu-items';
import { SearchBar } from '@/components/forms/search-bar';
import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useAppTheme } from '@/providers/theme-provider';
import { MenuItem } from '@/types/menu-item';
import { findSimilarItems } from '@/utils/fuzzy-match';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  Text,
  View,
} from 'react-native';
import { useSuggestMenuItem } from '../hooks/use-suggest-menu-item';


export interface MenuItemSearchSectionProps {
  /** Business ID to fetch menu items for */
  businessId: string | null;
  /** Business name shown as context in the suggest form */
  businessName?: string;
  /** Currently selected menu item */
  selectedMenuItem: MenuItem | null;
  /** Callback when a menu item is selected */
  onSelect: (menuItem: MenuItem) => void;
  /** Callback when selection is cleared */
  onClear: () => void;
}

export function MenuItemSearchSection({
  businessId,
  businessName,
  selectedMenuItem,
  onSelect,
  onClear,
}: MenuItemSearchSectionProps) {
  const theme = useAppTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(!selectedMenuItem);

  // Suggest form state
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [showSimilarWarning, setShowSimilarWarning] = useState(false);

  const suggestMutation = useSuggestMenuItem(businessId || '');

  // Debounce search query
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Fetch all menu items for the business
  const { data: menuItemsData, isLoading: isMenuItemsLoading } = useQuery({
    ...menuItemsListQueryOptions({ business_id: businessId!, per_page: 50, sort_by: 'name' }),
    enabled: !!businessId && isExpanded && !debouncedQuery.trim(),
  });

  // Search menu items
  const { data: searchResults, isLoading: isSearchLoading } = useQuery({
    ...menuItemsSearchQueryOptions({ query: debouncedQuery.trim(), business_id: businessId!, per_page: 50 }),
    enabled: !!businessId && isExpanded && debouncedQuery.trim().length >= 2,
  });

  const isSearching = debouncedQuery.trim().length >= 2;
  const displayItems = isSearching
    ? searchResults?.items || []
    : menuItemsData?.items || [];
  const isLoading = isSearching ? isSearchLoading : isMenuItemsLoading;

  // Fuzzy match against all loaded menu items
  const similarItems = useMemo(
    () => {
      const allItems = menuItemsData?.items || [];
      return findSimilarItems(newItemName, allItems, (item) => item.name);
    },
    [newItemName, menuItemsData?.items]
  );

  const handleSelect = useCallback(
    (menuItem: MenuItem) => {
      onSelect(menuItem);
      setIsExpanded(false);
      setSearchQuery('');
      setIsAddingItem(false);
      setNewItemName('');
      setNewItemDescription('');
      setShowSimilarWarning(false);
    },
    [onSelect]
  );

  const handleChange = useCallback(() => {
    setIsExpanded(true);
  }, []);


  const handleStartSuggest = useCallback(() => {
    setIsAddingItem(true);
    setNewItemName(searchQuery.trim());
    setNewItemDescription('');
    setShowSimilarWarning(false);
  }, [searchQuery]);

  const handleCancelSuggest = useCallback(() => {
    setIsAddingItem(false);
    setNewItemName('');
    setNewItemDescription('');
    setShowSimilarWarning(false);
  }, []);

  const handleNameChange = useCallback((text: string) => {
    setNewItemName(text);
    setShowSimilarWarning(false);
  }, []);

  const handleSuggestSubmit = useCallback(async () => {
    if (!newItemName.trim() || !businessId) return;

    // On first submit attempt, show similar items warning if any exist
    if (!showSimilarWarning && similarItems.length > 0) {
      setShowSimilarWarning(true);
      return;
    }

    try {
      const created = await suggestMutation.mutateAsync({
        business_id: businessId,
        name: newItemName.trim(),
        description: newItemDescription.trim() || undefined,
      });
      toast.success('Item suggested!');
      handleSelect(created);
    } catch {
      toast.error('Failed to suggest item.');
    }
  }, [
    newItemName,
    newItemDescription,
    businessId,
    showSimilarWarning,
    similarItems.length,
    suggestMutation,
    handleSelect,
  ]);

  // If no business is selected, show placeholder
  if (!businessId) {
    return (
      <View className="mb-4">
        <Text
          className="text-sm font-medium mb-2 text-muted-foreground"
          accessibilityRole="header"
        >
          What did you have?
        </Text>
        <View
          className="p-4 rounded-lg items-center justify-center bg-surface border border-dashed border-border"
          style={{ minHeight: 80 }}
        >
          <Ionicons
            name="restaurant-outline"
            size={24}
            className="text-muted-foreground"
          />
          <Text className="text-sm mt-2 text-center text-muted-foreground">
            Select a restaurant first
          </Text>
        </View>
      </View>
    );
  }

  // Collapsed card view
  if (selectedMenuItem && !isExpanded) {
    return (
      <View className="mb-4">
        <Text
          className="text-xl text-foreground mb-2"
          accessibilityRole="header"
        >
          Menu Item
        </Text>
        <View
          className="p-4 rounded-lg flex-row items-center justify-between bg-surface border border-border"
        >
          <View className="flex-1 mr-3">
            <View className="flex-row items-center">
              <Text className="text-base font-semibold text-foreground">
                {selectedMenuItem.name}
              </Text>
            </View>
            {selectedMenuItem.description && (
              <Text
                className="text-sm mt-1 text-muted-foreground"
                numberOfLines={1}
              >
                {selectedMenuItem.description}
              </Text>
            )}
          </View>
          <Pressable
            onPress={handleChange}
            className="px-3 py-1.5 rounded-full bg-primary/[0.09]"
            accessibilityRole="button"
            accessibilityLabel="Change menu item"
          >
            <Text className="text-sm font-medium text-primary">
              Change
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  // Expanded search view
  return (
    <View className="mb-4">
      <Text
        className="text-sm font-medium mb-2 text-muted-foreground"
        accessibilityRole="header"
      >
        What did you have?
      </Text>

      {/* Search Bar */}
      {!isAddingItem && (
        <SearchBar
          value={searchQuery}
          onChangeText={setSearchQuery}
          onClear={() => setSearchQuery('')}
          placeholder="Search menu items..."
        />
      )}

      {/* Inline Suggest Form */}
      {isAddingItem ? (
        <View className="p-4 rounded-lg bg-surface border border-border">
          <Text
            className="text-sm font-semibold mb-3 text-foreground"
            accessibilityRole="header"
          >
            Suggest a new item
          </Text>

          {businessName && (
            <View className="flex-row items-center rounded-lg px-3 py-2.5 mb-2 bg-muted/50">
              <Ionicons name="location-outline" size={14} className="text-muted-foreground" />
              <Text className="text-sm text-muted-foreground ml-1.5" numberOfLines={1}>
                {businessName}
              </Text>
            </View>
          )}

          <Text className="text-xs font-medium mb-1 text-muted-foreground">
            Item name
          </Text>
          <TextInput
            value={newItemName}
            onChangeText={handleNameChange}
            placeholder="e.g., Spicy Tuna Roll"
            autoFocus
            inputContainerStyle={{
              marginBottom: 8,
            }}
          />

          <Text className="text-xs font-medium mb-1 text-muted-foreground">
            Menu description (optional)
          </Text>
          <TextInput
            value={newItemDescription}
            onChangeText={setNewItemDescription}
            placeholder="e.g., Fresh tuna with avocado and sriracha mayo"
            multiline
            numberOfLines={2}
            inputContainerStyle={{
              marginBottom: 12,
            }}
            inputStyle={{
              minHeight: 60,
            }}
          />

          {/* Similar items warning */}
          {showSimilarWarning && similarItems.length > 0 && (
            <View className="bg-warning/[0.06] border border-warning/[0.19] rounded-lg p-3 mb-3">
              <Text className="text-sm font-medium mb-2 text-warning">
                Did you mean one of these?
              </Text>
              {similarItems.slice(0, 3).map((item) => (
                <Pressable
                  key={item.id}
                  onPress={() => handleSelect(item)}
                  className="py-2 px-3 rounded mb-1 bg-background"
                  accessibilityRole="button"
                  accessibilityLabel={`Select ${item.name}`}
                >
                  <View className="flex-row items-center">
                    <Text className="text-sm font-medium text-foreground">
                      {item.name}
                    </Text>
                  </View>
                </Pressable>
              ))}
              <Pressable
                onPress={handleSuggestSubmit}
                className="mt-2"
                accessibilityRole="link"
                accessibilityLabel="Suggest a new menu item"
              >
                <Text className="text-xs text-primary">
                  No, add &quot;{newItemName.trim()}&quot; as a new item
                </Text>
              </Pressable>
            </View>
          )}

          {/* Form buttons */}
          <View className="flex-row" style={{ gap: 8 }}>
            <Pressable
              onPress={handleCancelSuggest}
              className="flex-1 py-2.5 rounded-lg items-center justify-center bg-background border border-border"
              style={{ minHeight: 44 }}
              accessibilityRole="button"
            >
              <Text className="text-sm font-medium text-muted-foreground">
                Cancel
              </Text>
            </Pressable>
            <Pressable
              onPress={handleSuggestSubmit}
              disabled={!newItemName.trim() || suggestMutation.isPending}
              className={`flex-1 py-2.5 rounded-lg items-center justify-center ${!newItemName.trim() ? 'bg-border' : 'bg-primary'}`}
              style={{ minHeight: 44 }}
              accessibilityRole="button"
            >
              {suggestMutation.isPending ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text className="text-sm font-medium">
                  Suggest Item
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      ) : (
        <>
          {/* Section Label */}
          <Text className="text-xs font-medium mt-3 mb-2 text-muted-foreground">
            {isSearching ? 'SEARCH RESULTS' : 'MENU ITEMS'}
          </Text>

          {/* Menu Items List */}
          {isLoading ? (
            <View className="py-6 items-center">
              <ActivityIndicator size="small" color={theme.colors.primary} />
            </View>
          ) : displayItems.length > 0 ? (
            <>
              <FlatList
                data={displayItems}
                keyExtractor={(item) => item.id}
                scrollEnabled={false}
                renderItem={({ item }) => (
                  <Pressable
                    onPress={() => handleSelect(item)}
                    className="p-3 rounded-lg mb-2 bg-surface border border-border"
                    accessibilityRole="button"
                    accessibilityLabel={`Select ${item.name}`}
                  >
                    <View className="flex-row items-center justify-between">
                      <View className="flex-1">
                        <View className="flex-row items-center">
                          <Text className="font-medium text-foreground">
                            {item.name}
                          </Text>
                              </View>
                        {item.description && (
                          <Text
                            className="text-xs mt-1 text-muted-foreground"
                            numberOfLines={1}
                          >
                            {item.description}
                          </Text>
                        )}
                      </View>
                      <Ionicons
                        name="chevron-forward"
                        size={18}
                        className="text-muted-foreground"
                      />
                    </View>
                  </Pressable>
                )}
              />
              {/* Suggest link below results */}
              <Pressable
                onPress={handleStartSuggest}
                className="py-3 items-center"
                accessibilityRole="link"
                accessibilityLabel="Suggest a new menu item"
              >
                <Text className="text-sm text-primary">
                  Don&apos;t see your item? Suggest it
                </Text>
              </Pressable>
            </>
          ) : (
            <View className="py-6 items-center">
              <Ionicons
                name="fast-food-outline"
                size={32}
                className="text-muted-foreground"
              />
              <Text className="text-sm mt-2 text-center text-muted-foreground">
                {isSearching ? 'No items found' : 'No menu items yet'}
              </Text>
              <Pressable
                onPress={handleStartSuggest}
                className="mt-2"
                accessibilityRole="link"
                accessibilityLabel="Suggest a new menu item"
              >
                <Text className="text-sm font-medium text-primary">
                  {isSearching ? 'Suggest a new item' : 'Suggest the first item!'}
                </Text>
              </Pressable>
            </View>
          )}
        </>
      )}
    </View>
  );
}

export default MenuItemSearchSection;
